'use client';

import { useEffect, useRef } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';
import { pushTaboola, scheduleTaboolaFlush } from './TaboolaWidget';

// Article infinite-scroll placement ("Article Pages" section of taboola.txt).
// Only the "Below Article Thumbnails" widget is shown (the Mid / Right Rail
// placements were dropped). Each scrolled-in article is a new "page": the
// container id needs to be unique across the session, so we use the base id as
// a prefix and append the article's index (the same counter the page uses to
// decide URL swaps).
const PLACEMENTS = [{
  mode: 'alternating-thumbnails-a',
  base: 'taboola-below-article-thumbnails',
  placement: 'Below Article Thumbnails'
}] as const;
interface Props {
  /** Stable per-article identifier — makes each page's container ids unique.
   *  A plain incrementing index for the legacy infinite-scroll list, or a
   *  story id/slug for the dynamic article-template renderer (any value is
   *  fine — it's only ever interpolated into a container id string). */
  pageIndex: number | string;
  /** Canonical (non-AMP) URL of this article, passed to Taboola as article:auto context. */
  canonicalUrl: string;
}

/**
 * Renders the Taboola infinite-scroll placement for one article and runs
 * the onboarding "On Each New Page Scroll" sequence whenever the article
 * changes: push each widget (with its unique container), then re-declare
 * `{article:'auto', url}` for this page's canonical URL, then flush.
 *
 * Ordering matters to Taboola (widgets → article:auto+url → flush); doing it in
 * a single effect guarantees that order regardless of how React schedules the
 * surrounding DOM. Flush is debounced in TaboolaWidget so a burst of articles
 * scrolling in coalesces into one flush.
 *
 * Tracks the LAST canonicalUrl pushed (not just "ever pushed") so this keeps
 * working even if React reuses this component instance across a client-side
 * navigation instead of unmounting it — see the matching comment on
 * TaboolaRightRail.
 */
export default function TaboolaInfiniteScrollWidgets({
  pageIndex,
  canonicalUrl
}: Props) {
  const pushedForUrlRef = useRef<string | null>(null);
  const containers = PLACEMENTS.map(p => `${p.base}-${pageIndex}`);
  useEffect(() => {
    if (pushedForUrlRef.current === canonicalUrl || isLighthouseClient()) return;
    pushedForUrlRef.current = canonicalUrl;
    PLACEMENTS.forEach((p, i) => {
      pushTaboola({
        mode: p.mode,
        container: containers[i],
        placement: p.placement,
        target_type: 'mix'
      });
    });
    pushTaboola({
      article: 'auto',
      url: canonicalUrl
    });
    scheduleTaboolaFlush();
    // containers are derived from pageIndex, which changes in lockstep with canonicalUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalUrl]);
  return (
    // px-4 mirrors the standard article side padding (`p-4` used throughout
    // the article body sections) so the Taboola thumbnails don't bleed to
    // the screen edges.
    <div className="taboola-infinite-scroll-widgets w-full px-4">
      {containers.map(id => <div key={id} id={id} className="mt-4" />)}
    </div>
  );
}